import React, { useState, useEffect } from 'react';
import { useAppSelector } from '@/app/hooks';
import { makePostCall } from '@/services/makePostCall';
import { DefaultModal } from '@/components/Modals/DefaultModal/DefaultModal';
import { Button } from '@/components/Button/DefaultButton/Button';
import { TextInput } from '@/components/input/TextInput/TextInput';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateFunction } from 'react-router-dom';

import * as yup from 'yup';
import { Modal, ModalBody, ModalTitle, BackDrop, ModalFooter, ModalContent, TextBlock, BodyText, EmailText, Message, InputGroup } from './CodeInput.styles';
import { ModalSubmit } from '@/components/Modals/ModalSubmit/ModalSubmit';
import { pages } from '@/defaultVerbiages';
import { LoanApplicationState } from '@/reducers/LoanApplication.types';
import { Spinner } from '@/components/RefreshableCurrencyInput/RefreshableCurrencyInput.styles';
import { toPascalCase } from '@/utils/capitalizeWords';
import { monthsBetweenDateAndToday } from '@/utils/handleDate';

interface CodeProps {
  disabled?: boolean;
  loanApplication: LoanApplicationState;
  isResume: boolean;
  setIsResume: React.Dispatch<React.SetStateAction<boolean>>;
  onAccess: (page: string) => void;
  navigation?: NavigateFunction;
  children?: React.ReactNode;
}

const CodeInputV1: React.FC<CodeProps> = ({ isResume, setIsResume, loanApplication, onAccess, navigation }) => {
  const [value, setValue] = useState<string>('');
  const [validated, setValidated] = useState<boolean>(true);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [openSoftPull, setOpenSoftPull] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState(false);
  const [error, setError] = useState<string | unknown>(null);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);

  const currentForm = useSelector((state: any) => state.CurrentForm);
  const dispatch = useDispatch();
  const RESUME_CODE_REGEX = /^[a-zA-Z0-9]{6}$/;

  const yupParams = yup
    .string()
    .transform((value) => value.replace(/-/g, ''))
    .matches(RESUME_CODE_REGEX, { excludeEmptyString: false });
  const formSchema = yup.object().shape({
    'resume-code-input': yupParams,
  });

  const checkValidation = async (inputData: Record<string, string>) => {
    const varIsValid = await formSchema.isValid(inputData);
    setIsValid(varIsValid);
  };

  const sendCodeToEmail = async () => {
    setErrorCode(false);

    try {
      const endpoint = '/auth/sendCode';
      const payload = { email: currentForm.email };
      const newAction = {
        actionProperties: {
          endpoint,
        },
      };
      await makePostCall(newAction, loanApplication, payload, dispatch, templateConfig);
    } catch (error) {
      console.error('Error sending code to email:', error);
      setError(error);
      setOpenErrorModal(true);
    } finally {
    }
  };

  const verifyCode = async () => {
    setErrorCode(false);
    setLoading(true);

    try {
      const endpoint = '/auth/verifyCode';
      const payload = { email: currentForm.email, code: value };
      const newAction = {
        actionProperties: {
          endpoint,
        },
      };

      const response = await makePostCall(newAction, loanApplication, payload, dispatch, templateConfig);

      if (response) {
        const data = response.data;

        const formatDate = (dateStr: string) => {
          if (!dateStr) return '';
          const [year, month, day] = dateStr.split('-');
          return `${month}/${day}/${year}`;
        };

        const cleanMailingAddress = (mailing: any) => {
          if (!mailing) return {};

          return {
            mailingStreetAddress: mailing.mailingStreetAddress || '',
            mailingStreetAddress2: mailing.mailingStreetAddress2 || '',
            mailingCity: toPascalCase(mailing.mailingCity || ''),
            mailingState: mailing.mailingState || '',
            mailingCountry: toPascalCase(mailing.mailingCountry || ''),
            mailingCounty: toPascalCase(mailing.mailingCounty || ''),
            mailingZip: mailing.mailingZip || '',
            mailingLatitude: Number(mailing.mailingLatitude) || 0,
            mailingLongitude: Number(mailing.mailingLongitude) || 0,
          };
        };

        const updatedBorrowers = data?.borrowers?.map((borrower: any) => {
          const employmentHistory = borrower?.employmentHistory || [];
          let updatedEmploymentHistory = employmentHistory.map((emp: any) => {
            const formattedStartDate = formatDate(emp.startDate);
            const formattedEndDate = emp.endDate ? formatDate(emp.endDate) : undefined;
            const monthsDiff = monthsBetweenDateAndToday(formattedStartDate, 'startDate', emp.currentEmployment ? undefined : formattedEndDate);

            return {
              ...emp,
              employerName: emp.employerName || '',
              jobTitle: emp.jobTitle || '',
              startDate: formattedStartDate,
              employedMonths: monthsDiff,
              totalMonths: monthsDiff,
              professionMonths: monthsDiff,
              grossMonthlyIncome: Number(borrower.currentEmploymentGrossMonthlyIncomeTotal) || 0,
            };
          });

          if (employmentHistory.length === 0) {
            updatedEmploymentHistory = [
              {
                currentEmployment: null,
                employerName: '',
                endDate: '',
                grossMonthlyIncome: 0,
                jobTitle: '',
                startDate: '',
                type: '',
              },
            ];
          }

          const { creditScore, ...borrowerWithoutCreditScore } = borrower;

          const borrowerData = {
            ...borrowerWithoutCreditScore,
            borrowerFirstName: toPascalCase(borrower.borrowerFirstName || ''),
            borrowerLastName: toPascalCase(borrower.borrowerLastName || ''),
            borrowerEmailAddress: borrower.borrowerEmailAddress?.toLowerCase() || '',
            borrowerLoanPurpose: toPascalCase(borrower.borrowerLoanPurpose || ''),
            borrowerCitizenship: toPascalCase(borrower.borrowerCitizenship || ''),
            currentEmploymentGrossMonthlyIncomeTotal: Number(borrower.currentEmploymentGrossMonthlyIncomeTotal) || 0,
            employmentHistory: updatedEmploymentHistory,
            borrowerDOB: formatDate(borrower.borrowerDOB),
            isPrimary: borrower.isPrimary || false,
            isSpouse: borrower.isSpouse || false,
            declaration: borrower.declaration || {},
            governmentHMDA: borrower.governmentHMDA || { applicantRace: [], applicantEthnicity: [], applicantSex: [] },
            creditScore: Number(creditScore) || 0,
            borrowerPrimaryPhone: borrower.borrowerPrimaryPhone || '',
            borrowerSSN: borrower.borrowerSSN || '',
          };

          if (borrower.borrowerMiddleName) borrowerData.borrowerMiddleName = toPascalCase(borrower.borrowerMiddleName);
          if (borrower.borrowerSuffixName) borrowerData.borrowerSuffixName = toPascalCase(borrower.borrowerSuffixName);
          borrowerData.borrowerPreferredContactMethod = borrower.borrowerPreferredContactMethod ? toPascalCase(borrower.borrowerPreferredContactMethod) : '';
          borrowerData.borrowerMaritalStatus = borrower.borrowerMaritalStatus ? toPascalCase(borrower.borrowerMaritalStatus) : '';
          if (borrower.borrowerRelationship) borrowerData.borrowerRelationship = toPascalCase(borrower.borrowerRelationship);
          if (borrower.borrowerPrimaryPhone) borrowerData.borrowerPrimaryPhone = borrower.borrowerPrimaryPhone;
          if (borrower.borrowerCitizenship) borrowerData.borrowerCitizenship = toPascalCase(borrower.borrowerCitizenship);
          if (borrower.borrowerSSN) borrowerData.borrowerSSN = borrower.borrowerSSN;

          return borrowerData;
        });

        const updatedState = {
          ...loanApplication,
          loanId: data.loanId || '',
          loanNumber: data.loanNumber || '',
          loanType: data.loanType || '',
          estimatedMarketValue: Number(data.equityResponse?.estimatedMarketValue) || 0,
          remainingMortgageBalance: Number(data.equityResponse?.remainingMortgageBalance) || 0,
          estimatedHomeEquity: Number(data.equityResponse?.estimatedHomeEquity) || 0,
          estimatedRawEquity: Number(data.equityResponse?.estimatedRawEquity) || Number(data?.estimatedRawEquity) || 0,
          lineAmount: Number(data.lineAmount) || 0,
          drawAmount: Number(data.drawAmount) || 35000,
          ...(data?.drawAmount && { initialDrawAmount: Number(data.drawAmount) }),
          ...(data?.lineAmount && { initialLineAmount: Number(data.lineAmount) }),
          apr: Number(data.apr) || 0,
          term: data.term || 'Variable',
          loanTerm: data.loanTerm || '0',
          ipAddress: data.ipAddress || '',
          propertyPrimaryUse: toPascalCase(data.propertyPrimaryUse || ''),
          propertyType: toPascalCase(data.propertyType || ''),
          isResume: false,
          isEligible: false,
          minDrawAmount: 0,
          maxDrawAmount: 0,
          borrowers: updatedBorrowers,
          borrowerProperty: {
            propertyStreetAddress: data.borrowerProperty?.propertyStreetAddress || '',
            propertyStreetAddress2: data.borrowerProperty?.propertyStreetAddress2 || '',
            propertyCity: toPascalCase(data.borrowerProperty?.propertyCity || ''),
            propertyState: data.borrowerProperty?.propertyState || '',
            propertyCountry: toPascalCase(data.borrowerProperty?.propertyCountry || 'United States'),
            propertyCounty: toPascalCase(data.borrowerProperty?.propertyCounty || ''),
            propertyZip: data.borrowerProperty?.propertyZip || '',
            propertyLatitude: Number(data.borrowerProperty?.propertyLatitude) || 0,
            propertyLongitude: Number(data.borrowerProperty?.propertyLongitude) || 0,
            propertyOccupancyDate: data.borrowerProperty?.propertyOccupancyDate || '1990-01-01',
            city: toPascalCase(data.borrowerProperty?.city || data.borrowerProperty?.propertyCity || ''),
            state: data.borrowerProperty?.state || data.borrowerProperty?.propertyState || '',
            zip: data.borrowerProperty?.zip || data.borrowerProperty?.propertyZip || '',
          },
          mailingAddress: cleanMailingAddress(data.mailingAddress),
          consent: {
            ...loanApplication.consent,
            eConsent: data.consent?.eConsent || true,
            eConsentDate: data.consent?.eConsentDate || null,
            tcpaConsent: data.consent?.tcpaConsent || false,
            softPullConsent: data.consent?.softPullConsent || false,
            softPullConsentDate: data.consent?.softPullConsentDate || null,
            employmentConsent: data.consent?.employmentConsent || false,
            eConsentCoborrower: data.consent?.eConsentCoborrower || false,
          },
          selectedProgramName: toPascalCase(data.selectedProgramName || 'HELOC - Standard'),
          selectedProgram: {},
          availablePrograms: {},
          propertyIsMailingAddress: true,
          fixedLineAmount: 35000,
        };

        dispatch({
          type: 'LoanApplication/updateLoanApplication',
          payload: updatedState,
        });

        const primaryBorrower = data.borrowers.find((b: any) => b.isPrimary) || data.borrowers[0];
        const currentFormPayload = {
          firstName: toPascalCase(primaryBorrower.borrowerFirstName || ''),
          middleName: toPascalCase(primaryBorrower.borrowerMiddleName || ''),
          lastName: toPascalCase(primaryBorrower.borrowerLastName || ''),
          email: primaryBorrower.borrowerEmailAddress?.toLowerCase() || '',
          purposeOfLoan: toPascalCase(primaryBorrower.borrowerLoanPurpose || ''),
          address: toPascalCase(data.borrowerProperty?.propertyStreetAddress || ''),
          suit: toPascalCase(data.borrowerProperty?.propertyStreetAddress2 || ''),
          city: toPascalCase(data.borrowerProperty?.propertyCity || ''),
          state: toPascalCase(data.borrowerProperty?.propertyState || ''),
          zip: data.borrowerProperty?.propertyZip || '',
          mailingCity: toPascalCase(data.mailingAddress?.mailingCity || ''),
          mailingState: toPascalCase(data.mailingAddress?.mailingState || ''),
          mailingZip: data.mailingAddress?.mailingZip || '',
          primaryUse: toPascalCase(data.propertyPrimaryUse || ''),
          propertyType: toPascalCase(data.propertyType || ''),
          dataOccupancyDate: data.borrowerProperty?.propertyOccupancyDate || '1990-01-01',
          mobile: primaryBorrower.borrowerPrimaryPhone || '',
          home: primaryBorrower.borrowerAlternatePhone || '',
          work: primaryBorrower.borrowerWorkPhone || '',
          eConsent: data.consent?.eConsent || true,
          tcpaConsent: data.consent?.tcpaConsent || false,
          softPullConsent: data.consent?.softPullConsent || false,
          employmentConsent: data.consent?.employmentConsent || false,
          eConsentCoborrower: data.consent?.eConsentCoborrower || false,
          isMailingAddressSame: data.mailingAddress?.mailingAddressSameAsProperty || false,
          propertyOccupancyDate: data.borrowerProperty?.propertyOccupancyDate || '1990-01-01',
          propertyLatitude: Number(data.borrowerProperty?.propertyLatitude) || 0,
          propertyLongitude: Number(data.borrowerProperty?.propertyLongitude) || 0,
          propertyStreetAddress2: toPascalCase(data.borrowerProperty?.propertyStreetAddress2 || ''),
          propertyCountry: toPascalCase(data.borrowerProperty?.propertyCountry || 'United States'),
          propertyCounty: toPascalCase(data.borrowerProperty?.propertyCounty || ''),
          propertyZip: data.borrowerProperty?.propertyZip || '',
          estimatedMarketValue: Number(data.equityResponse?.estimatedMarketValue) || Number(data.estimatedMarketValue) || 0,
          remainingMortgageBalance: Number(data.equityResponse?.remainingMortgageBalance) || Number(data.remainingMortgageBalance) || 0,
          estimatedHomeEquity: Number(data.equityResponse?.estimatedRawEquity) || Number(data.estimatedRawEquity) || 0,
        };

        dispatch({
          type: 'CurrentForm/addField',
          payload: currentFormPayload,
        });

        setValidated(true);
        setLoading(false);

        const boappLastStep = data.boappLastStep;

        if (boappLastStep) {
          const pageRoute = getPageRouteFromBoappLastStep(boappLastStep);

          if (pageRoute) {
            markAllPreviousPagesAsAccessed(boappLastStep);

            closeModal();

            setTimeout(() => {
              if (navigation && typeof navigation === 'function') {
                navigation(pageRoute);
              } else {
                window.location.href = pageRoute;
              }
            }, 100);
          } else {
            closeModal();
          }
        } else {
          closeModal();
        }
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError(error);
      setOpenErrorModal(true);
      setValidated(false);
      setErrorCode(true);
    } finally {
      setLoading(false);
    }
  };

  const getPageRouteFromBoappLastStep = (boappLastStep: string): string | null => {
    const pageMapping: Record<string, string> = {
      GetStarted: '/get-started',
      GetStartedAddress: '/get-started-address',
      EstimatedAvailableEquity: '/estimated-available-equity',
      Vmo: '/vmo',
      VmoLineAmount: '/vmo-line-amount',
      VmoDrawAmount: '/vmo-draw-amount',
      PersonalInformation: '/personal-information',
      EmploymentInformation: '/employment-information',
      GovernmentMonitoringInformation: '/government-monitoring-information',
      Declarations: '/declarations',
      InquirySummary: '/inquiry-summary',
      Completion: '/completion',
    };

    return pageMapping[boappLastStep] || null;
  };

  const markAllPreviousPagesAsAccessed = (boappLastStep: string) => {
    const pageOrder = [
      'get-started',
      'get-started-address',
      'estimated-available-equity',
      'vmo',
      'vmo-line-amount',
      'vmo-draw-amount',
      'personal-information',
      'employment-information',
      'government-monitoring-information',
      'declarations',
      'inquiry-summary',
      'completion',
    ];

    const boappLastStepToPageRoute: Record<string, string> = {
      GetStarted: 'get-started',
      GetStartedAddress: 'get-started-address',
      EstimatedAvailableEquity: 'estimated-available-equity',
      Vmo: 'vmo',
      VmoLineAmount: 'vmo-line-amount',
      VmoDrawAmount: 'vmo-draw-amount',
      PersonalInformation: 'personal-information',
      EmploymentInformation: 'employment-information',
      GovernmentMonitoringInformation: 'government-monitoring-information',
      Declarations: 'declarations',
      InquirySummary: 'inquiry-summary',
      Completion: 'completion',
    };

    const targetPageRoute = boappLastStepToPageRoute[boappLastStep];

    if (targetPageRoute) {
      const targetIndex = pageOrder.indexOf(targetPageRoute);

      if (targetIndex !== -1) {
        const pagesToMark: string[] = [];
        for (let i = 0; i <= targetIndex; i++) {
          const pageRoute = `/${pageOrder[i]}`;
          pagesToMark.push(pageRoute);
        }

        pagesToMark.forEach((pageRoute) => {
          onAccess(pageRoute);
        });
      }
    }
  };

  const closeModal = () => {
    setOpenSoftPull(true);
    if (setIsResume) setIsResume(false);
  };

  useEffect(() => {
    sendCodeToEmail();
  }, []);

  return (
    <>
      <BackDrop $isOpen={isResume} onClick={() => closeModal()} data-testid="modal-backdrop" />
      <Modal $isOpen={isResume}>
        <ModalBody>
          <ModalTitle>{templateConfig?.pages?.LetsGetStarted?.modals?.resumeInquiry?.title || 'Existing Loan Inquiry Found'}</ModalTitle>
          <ModalContent>
            <BodyText>
              {templateConfig?.pages?.LetsGetStarted?.modals?.resumeInquiry?.subtitle ||
                `We found an existing loan inquiry associated with this email. To continue, enter the 6-digit code sent to your email and click 'Resume My Inquiry'.`}
            </BodyText>
            <TextBlock>
              <EmailText>{currentForm.email}</EmailText>
            </TextBlock>

            <InputGroup>
              <TextInput
                placeholder={'--- ---'}
                size={'32%'}
                propertyType={'code-input'}
                onChange={(values) => {
                  setValue(values.toString().replace(/-/g, ''));
                  checkValidation({ 'resume-code-input': values.toString() });
                }}
              />
            </InputGroup>

            <Message $validated={validated}>
              {validated
                ? templateConfig?.pages?.LetsGetStarted?.modals?.resumeInquiry?.expiredCode || 'This code expires after one-time use.'
                : templateConfig?.pages?.LetsGetStarted?.modals?.resumeInquiry?.incorrectCode || 'Incorrect code. Please check and try again.'}
            </Message>
          </ModalContent>
        </ModalBody>
        <ModalFooter>
          <Button
            label={!loading ? templateConfig?.pages?.LetsGetStarted?.modals?.resumeInquiry?.resumeButton || 'Resume My Inquiry' : <Spinner />}
            onClick={verifyCode}
            isDisabled={!isValid || loading}
            variant="primary"
            buttonSize="l"
          />

          <BodyText>
            {errorCode
              ? templateConfig?.pages?.LetsGetStarted?.modals?.resumeInquiry?.incorrectCode
              : `To start a new inquiry, click 'Restart My Loan Inquiry' below. (Creating a new inquiry will archive the existing one.`}
          </BodyText>

          <Button
            label={templateConfig?.pages?.LetsGetStarted?.modals?.resumeInquiry?.restartButton || 'Restart My Inquiry'}
            onClick={() => closeModal()}
            variant="primary"
            buttonSize="l"
          />
        </ModalFooter>
      </Modal>
      {openSoftPull && (
        <ModalSubmit
          openModal={openSoftPull}
          setOpenModal={() => {}}
          infoSubmitModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoSubmitModal || pages?.LetsGetStarted?.modals?.infoSubmitModal}
          onAccess={onAccess}
        />
      )}
      {openErrorModal && error && (
        <DefaultModal
          openModal={openErrorModal}
          setOpenModal={setOpenErrorModal}
          isErrorModal={true}
          infoModal={{
            header: (error as any)?.errorResponse?.response?.error?.appActionTitle || 'Error',
            body: typeof error === 'string' ? error : (error as any)?.message || 'An error occurred',
            button: (error as any)?.errorResponse?.response?.error?.appActionButtonTitle || 'Return to %%lenderName%%',
          }}
          preventBackgroundInteraction={true}
        />
      )}
    </>
  );
};

export default CodeInputV1;
